import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  arrayOf, bool, exact, func, number, string,
} from 'prop-types';
import {
  Flex, Typography, Icon, Loader,
} from '@atoms';
import { Header, Search, ErrorToast } from '@molecules';
import { ValueProcessModal } from '@organisms';
import { formatPrice } from '@helpers/menuItemDetails';
import { ReactComponent as PlusIcon } from '@assets/plus.svg';
import { ReactComponent as ArrowIcon } from '@assets/arrow-down.svg';

const OptionsList = ({
  onValueDelete,
  isValueEditing,
  isValueDeleteModalOpen,
  onValueDeleteModalChangeState,
  onValueEditSubmit,
  valueName,
  valueImage,
  valuePrice,
  isValuePreselected,
  onValueImageChange,
  onValueNameChange,
  onValuePriceChange,
  onValueModalClose,
  isValueModalOpen,
  onValuePreselectedStateChange,
  optionList,
  optionsForSearch,
  expandedOptionId,
  error,
  isLoading,
  onToastClose,
  onOptionClick,
  onBackUpClick,
  onOptionValueClick,
  onCreateOptionClick,
  onCreateOptionValueClick,
  onSearch,
  onDropDownClick,
}) => {
  const memoizedOptionsList = useMemo(() => optionList.map(option => (
    <>
      <OptionsListItem
        marginTop="lg"
        paddingBottom="lg"
        paddingRight="sm"
        row
        spaceBetween
        itemsCenter
        key={`option-list-item-${option.id}`}
      >
        <ConstrainedSizeTextWrapper
          width="90%"
          column
          justifyStart
          itemsStart
          shrink={0}
          onClick={onOptionClick(option.id)}
        >
          <Typography size="xl" weight="medium">{option.name}</Typography>
          <Typography size="lg" color="warmGrey">{option.optionItemNames}</Typography>
        </ConstrainedSizeTextWrapper>
        <SizedFlex width="10%" row justifyEnd itemsStart shrink={0} onClick={onDropDownClick(option.id)}>
          <StyledArrow SVG={ArrowIcon} width="20px" height="20px" isExpanded={option.id === expandedOptionId} />
        </SizedFlex>
      </OptionsListItem>
      {expandedOptionId === option.id
        ? (
          <SizedFlex width="100%" column justifyStart itemsStart paddingLeft="lg">
            <OptionsListItem
              onClick={onCreateOptionValueClick}
              marginTop="lg"
              paddingBottom="lg"
              row
              justifyStart
              itemsCenter
              key="create-new-option-value-button"
            >
              <IconContainer grow={0} marginRight="xl4" padding="xxs">
                <Icon SVG={PlusIcon} width="15px" height="15px" />
              </IconContainer>
              <Typography size="lg" weight="medium">
                Add New Option Value
              </Typography>
            </OptionsListItem>
            {option.optionValues.map(optionValue => (
              <OptionsListItem
                marginTop="lg"
                paddingBottom="lg"
                row
                spaceBetween
                itemsCenter
                onClick={onOptionValueClick(optionValue.id)}
                key={`option-${option.id}-value-${optionValue.id}`}
              >
                <SizedFlex itemsCenter width="75%">
                  <RedDot grow={0} shrink={0} marginRight="lg" marginLeft="sm" />
                  <Typography size="lg">{optionValue.name}</Typography>
                </SizedFlex>
                <SizedFlex width="25%" row justifyEnd>
                  <Typography size="lg">{`$${formatPrice(optionValue.price)}`}</Typography>
                </SizedFlex>
              </OptionsListItem>
            ))}
          </SizedFlex>
        )
        : null}
    </>
  )), [optionList, expandedOptionId]);

  return (
    <>
      <SizedFlex width="100%" column justifyCenter itemsCenter>
        <Header title="Add an Option" onBackUpClick={onBackUpClick} />
        <Search listItems={optionsForSearch} onSearch={onSearch} />
        <SizedFlex width="100%" column justifyStart itemsCenter paddingLeft="md" paddingRight="md">
          <OptionsListItem
            marginTop="lg"
            paddingBottom="lg"
            row
            justifyStart
            itemsCenter
            onClick={onCreateOptionClick}
            key="create-new-option-button"
          >
            <IconContainer grow={0} marginLeft="xl" marginRight="xl4" padding="xxs">
              <Icon SVG={PlusIcon} width="20px" height="20px" />
            </IconContainer>
            <Typography size="xl" weight="medium">
              Add New Option
            </Typography>
          </OptionsListItem>
          {memoizedOptionsList}
        </SizedFlex>
      </SizedFlex>
      <ValueProcessModal
        onValueDelete={onValueDelete}
        IsValueDeleteModalOpen={isValueDeleteModalOpen}
        onValueDeleteModalChangeState={onValueDeleteModalChangeState}
        onValueEditSubmit={onValueEditSubmit}
        isEditing={isValueEditing}
        valueName={valueName}
        valueImage={valueImage}
        valuePrice={valuePrice}
        isValuePreselected={isValuePreselected}
        onValueImageChange={onValueImageChange}
        onValueNameChange={onValueNameChange}
        onValuePriceChange={onValuePriceChange}
        onModalClose={onValueModalClose}
        isModalOpen={isValueModalOpen}
        onValuePreselectedStateChange={onValuePreselectedStateChange}
      />
      {isLoading && <Loader />}
      {error && <ErrorToast errorMessage={error} onToastClose={onToastClose} />}
    </>
  );
};

const SizedFlex = styled(Flex)`
  ${({ width }) => css`
    width: ${width};
    max-width: ${width};
  `}
`;

const OptionsListItem = styled(Flex)`
  ${({ theme: { color } }) => css`
    width: 100%;
    border-bottom: 1px solid ${color.warmGrey};
  `}
`;

const IconContainer = styled(Flex)`
  ${({ theme: { border, color } }) => css`
    border-radius: ${border.radius.circular};
    background-color: ${color.grannySmith};
  `}
`;

const ConstrainedSizeTextWrapper = styled(SizedFlex)`
  ${({ width }) => css`
    ${Typography} {
      max-width: ${width};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

const StyledArrow = styled(Icon)`
  ${({ isExpanded }) => css`
    ${isExpanded && 'transform: rotate(180deg);'}
    transition: transform 0.5s;
  `}
`;

const RedDot = styled(Flex)`
  ${({ theme: { border, color, spacing } }) => css`
    width: ${spacing.sm};
    height: ${spacing.sm};
    border-radius: ${border.radius.circular};
    background-color: ${color.strawberry};
  `}
`;

OptionsList.defaultProps = {
  error: null,
  expandedOptionId: null,
};

OptionsList.propTypes = {
  isValueEditing: bool.isRequired,
  onValueDelete: func.isRequired,
  isValueDeleteModalOpen: bool.isRequired,
  onValueDeleteModalChangeState: func.isRequired,
  onValueEditSubmit: func.isRequired,
  valueName: string.isRequired,
  valueImage: string.isRequired,
  valuePrice: string.isRequired,
  isValuePreselected: bool.isRequired,
  onValueImageChange: func.isRequired,
  onValueNameChange: func.isRequired,
  onValuePriceChange: func.isRequired,
  onValueModalClose: func.isRequired,
  isValueModalOpen: bool.isRequired,
  onValuePreselectedStateChange: func.isRequired,
  optionList: arrayOf(exact({
    id: number.isRequired,
    name: string.isRequired,
    optionItemNames: string.isRequired,
    optionValues: arrayOf(exact({
      id: number.isRequired,
      name: string.isRequired,
      price: number.isRequired,
    })).isRequired,
  })).isRequired,
  optionsForSearch: arrayOf(exact({
    id: number.isRequired,
    name: string.isRequired,
  })).isRequired,
  expandedOptionId: number,
  error: string,
  isLoading: bool.isRequired,
  onToastClose: func.isRequired,
  onBackUpClick: func.isRequired,
  onOptionClick: func.isRequired,
  onOptionValueClick: func.isRequired,
  onCreateOptionClick: func.isRequired,
  onCreateOptionValueClick: func.isRequired,
  onSearch: func.isRequired,
  onDropDownClick: func.isRequired,
};

export default OptionsList;
